import { isDefined } from "@homebound/activesupport";
import { column, Css, GridColumn, GridDataRow, GridTable, Icon, numericColumn, simpleHeader } from "@homebound/beam";
import { useMemo } from "react";
import { CostType } from "src/generated/graphqlTypes";
import { getOptionsKey } from "src/routes/bids/components/BidsTab";
import { createProductOfferingScopeUrl } from "src/RouteUrls";
import { openNewTab } from "src/utils/window";
import {
  PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment,
  PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment,
} from "./PlanBasedBidsTableV2.generated";

type PlanBasedBidsUnmatchedScopeTableProps = {
  scopedLineItems: PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment[];
  searchFilter?: string;
};

export function PlanBasedBidsUnmatchedScopeTable(props: PlanBasedBidsUnmatchedScopeTableProps) {
  const { scopedLineItems, searchFilter } = props;
  const unmatchedScopeLines = useMemo(
    () =>
      scopedLineItems
        // Pull the takeoff line items from the single unmatched bucket (groupLine == null)
        .filter((sli) => !isDefined(sli.groupLine))
        .flatMap((sli) => sli.takeoffLineItems),
    [scopedLineItems],
  );
  if (unmatchedScopeLines.isEmpty) return null;
  return <PlanBasedBidsUnmatchedScopeTableView unmatchedScopeLines={unmatchedScopeLines} searchFilter={searchFilter} />;
}

type PlanBasedBidsUnmatchedScopeTableViewProps = {
  unmatchedScopeLines: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment[];
  searchFilter?: string;
};

type HeaderRow = { kind: "header"; data: undefined };
type DataRow = { kind: "data"; data: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment };
type Row = HeaderRow | DataRow;

function PlanBasedBidsUnmatchedScopeTableView(props: PlanBasedBidsUnmatchedScopeTableViewProps) {
  const { unmatchedScopeLines, searchFilter } = props;
  // Group the uncosted takeoff lines by their option combo so each unique combo gets its own table.
  const combos = useMemo(
    () =>
      unmatchedScopeLines
        .groupBy((tli) => getOptionsKey(tli.options))
        .toEntries()
        // Sort alphabetically by title so the tables render in a stable order.
        .sortBy(([, tlis]) => comboTitle(tlis[0].options)),
    [unmatchedScopeLines],
  );
  const columns = useMemo(() => createColumns(), []);
  return (
    <div css={Css.mb2.$}>
      <div css={Css.df.aic.gap1.p1.$}>
        <Icon icon="error" />
        <span data-testid="uncostedOptionsHeader" css={Css.smSb.gray800.$}>
          Uncosted options
        </span>
      </div>
      {combos.map(([key, tlis]) => (
        <div key={key} css={Css.mb2.$}>
          {/* Title each table with the option combo it represents, styled like the main table header. */}
          <div css={Css.df.aic.gap2.px2.py1.bgWhite.bb.bcGray200.$}>
            <span data-testid="uncostedComboTitle" css={Css.mdSb.gray900.$}>
              {comboTitle(tlis[0].options)}
            </span>
          </div>
          <GridTable id={`uncostedScopeTable-${key}`} columns={columns} rows={createRows(tlis)} filter={searchFilter} />
        </div>
      ))}
    </div>
  );
}

// Open the takeoff line item in a new tab, pre-filtered to its cost code and searched by its
// material/item code. Shared by the uncosted tables here and the nested scope lines in the main table.
export function openTakeoffLineInNewTab(tli: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment): void {
  const url = createProductOfferingScopeUrl(tli.readyPlan.id, tli.readyPlan.version.id, {
    search: tli.materialVariant?.code ?? tli.item.fullCode,
    filter: { costCode: [tli.item.costCode.id] },
  });
  openNewTab(url);
}

// Build the table title from the combo's option names; falls back to "No options" for lines with none.
function comboTitle(options: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment["options"]): string {
  return options.isEmpty
    ? "No options"
    : options
        .sortByKey("order")
        .map((o) => o.name)
        .join(" + ");
}

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Item Code",
      data: (tli) => ({
        content: `${tli.item.fullCode} ${tli.item.name}`,
        onClick: () => openTakeoffLineInNewTab(tli),
      }),
    }),
    column<Row>({ header: "Name", data: (tli) => tli.name }),
    column<Row>({ header: "Cost Type", data: (tli) => tli.costTypeDetail.name }),
    column<Row>({ header: "Location", data: (tli) => tli.location.displayLocationPath }),
    column<Row>({
      header: "Material/Labor",
      // Labor lines have no material variant, so just label them "Labor"; everything else shows its material code
      data: (tli) => (tli.costTypeDetail.code === CostType.Labor ? "Labor" : tli.materialVariant?.code),
    }),
    numericColumn<Row>({
      header: "QTY",
      // "N/A" for units that ignore quantity (e.g. Lump Sum/Allowance), matching the takeoff scope table
      data: (tli) => (tli.unitOfMeasure.useQuantity ? (tli.quantity ?? "") : "N/A"),
    }),
    column<Row>({ header: "UoM", data: (tli) => tli.unitOfMeasure.abbreviation }),
  ];
}

function createRows(unmatchedScopeLines: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...unmatchedScopeLines.map((tli) => ({ kind: "data" as const, id: tli.id, data: tli }))];
}
